import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";

import {
  dismissSyncChange,
  fetchSyncChanges,
  fetchSyncStatus,
  retrySyncChange,
} from "./sync-api";
import type { SyncChange, SyncStatus } from "./sync-state";
import { withSettledChanges } from "./sync-state";
import { useAuth } from "./useAuth";

/** ARD 9.6: poll fast while work is queued, slowly when the queue is idle. */
export const SYNC_POLL_BUSY_MS = 3000;
export const SYNC_POLL_IDLE_MS = 30000;

/** ARD 9.6: poll only while the tab is visible. */
export const tabVisible = (): boolean => {
  if (typeof document === "undefined") return true;
  return document.visibilityState !== "hidden";
};

export const syncStatusKey = (datasourceId: string) => [
  "syncStatus",
  datasourceId,
];

export const isSyncBusy = (status: SyncStatus | undefined): boolean =>
  (status?.pending ?? 0) + (status?.syncing ?? 0) > 0;

const useTabVisible = (): boolean => {
  const [visible, setVisible] = useState(tabVisible);

  useEffect(() => {
    if (typeof document === "undefined") return;
    const onChange = () => setVisible(tabVisible());
    document.addEventListener("visibilitychange", onChange);
    return () => document.removeEventListener("visibilitychange", onChange);
  }, []);

  return visible;
};

/**
 * `GET /sync/:ds/status` on the ARD 9.6 cadence: every 3 s while the queue has
 * work, else every 30 s, and never while the tab is hidden.
 */
export const useSyncStatus = (
  datasourceId: string | undefined,
  enabled: boolean
) => {
  const { token } = useAuth();
  const visible = useTabVisible();

  return useQuery({
    queryKey: syncStatusKey(datasourceId ?? ""),
    queryFn: () => fetchSyncStatus(datasourceId as string, { token }),
    enabled: !!datasourceId && enabled && visible,
    refetchInterval: (query) => {
      if (!visible) return false;
      const data = query.state.data as SyncStatus | undefined;
      return isSyncBusy(data) ? SYNC_POLL_BUSY_MS : SYNC_POLL_IDLE_MS;
    },
    refetchIntervalInBackground: false,
    staleTime: 0,
  });
};

/**
 * The queued and failed changes for one datasource. They ride the same poll as
 * the status call, because the retry countdown reads `nextAttemptAt`.
 *
 * ARD 9.6: 3 s while `pending + syncing > 0`, else 30 s, and never while the
 * tab is hidden. The busy flag comes from the status query: the changes answer
 * cannot see that a change is queued before it lands.
 *
 * ARD 9.4: an item that lands leaves this list, because the request asks for
 * `PENDING,SYNCING,FAILED` only. `withSettledChanges` keeps it as `SYNCED` with
 * `syncedAt` for the 3 s marker.
 */
export const useSyncChanges = (
  datasourceId: string | undefined,
  enabled: boolean,
  busy = false
) => {
  const { token } = useAuth();
  const visible = useTabVisible();
  const queryClient = useQueryClient();
  // The status poll is the busy flag. When it flips to idle, this query's
  // interval jumps from 3 s to 30 s and drops the tick that was already due.
  // A row can land in that gap and the marker keeps the old countdown. One
  // refetch reads the empty queue and lets that row show Synced.
  const wasBusy = useRef(false);
  useEffect(() => {
    const leftQueue = wasBusy.current && !busy;
    wasBusy.current = busy;
    if (!leftQueue || !datasourceId || !enabled || !visible) return;
    queryClient.invalidateQueries({
      queryKey: ["syncChanges", datasourceId],
    });
  }, [busy, datasourceId, enabled, visible, queryClient]);

  return useQuery({
    queryKey: ["syncChanges", datasourceId ?? ""],
    queryFn: async ({ client }) => {
      const next = await fetchSyncChanges(datasourceId as string, { token });
      const previous = client.getQueryData<SyncChange[]>([
        "syncChanges",
        datasourceId ?? "",
      ]);
      return withSettledChanges(previous, next, Date.now());
    },
    enabled: !!datasourceId && enabled && visible,
    refetchInterval:
      visible && busy ? SYNC_POLL_BUSY_MS : visible ? SYNC_POLL_IDLE_MS : false,
    refetchIntervalInBackground: false,
    staleTime: 0,
  });
};

/**
 * When `lastSyncedAt` moves, the table holds values that landed in Google
 * after it was read, so drop it. The first answer only sets the baseline.
 */
export const useInvalidateOnSync = (
  datasourceId: string | undefined,
  lastSyncedAt: string | null | undefined
) => {
  const queryClient = useQueryClient();
  const seen = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    if (seen.current === undefined) {
      seen.current = lastSyncedAt ?? null;
      return;
    }
    if ((lastSyncedAt ?? null) === seen.current) return;
    seen.current = lastSyncedAt ?? null;
    queryClient.invalidateQueries({
      queryKey: ["tableData", datasourceId],
    });
  }, [datasourceId, lastSyncedAt, queryClient]);
};

/** Retry and dismiss calls, then refresh the queue, the header and the table. */
export const useSyncActions = (datasourceId: string | undefined) => {
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const refresh = () => {
    queryClient.invalidateQueries({
      queryKey: syncStatusKey(datasourceId ?? ""),
    });
    queryClient.invalidateQueries({
      queryKey: ["syncChanges", datasourceId ?? ""],
    });
    queryClient.invalidateQueries({ queryKey: ["tableData", datasourceId] });
  };

  const retry = useMutation({
    mutationFn: (changeId: string) => retrySyncChange(changeId, { token }),
    onSettled: refresh,
  });
  const dismiss = useMutation({
    mutationFn: (changeId: string) => dismissSyncChange(changeId, { token }),
    onSettled: refresh,
  });

  return useMemo(
    () => ({
      retry: (changeId: string) => retry.mutate(changeId),
      dismiss: (changeId: string) => dismiss.mutate(changeId),
    }),
    [retry, dismiss]
  );
};
